import { supabase, supabaseAdmin } from '../supabaseClient.js';

/**
 * Supabase Database Service for School Deadlines
 * Handles storage and retrieval of deadline information
 */

/**
 * Ensures the school_deadlines table exists
 * This creates the table if it doesn't exist yet
 */
export async function initializeDatabase() {
    // Note: In production, you should create tables via Supabase Dashboard or migrations
    // This is just a reference for the schema needed
    const schema = `
    CREATE TABLE IF NOT EXISTS school_deadlines (
      id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
      school_name TEXT NOT NULL,
      deadline_date DATE,
      event_type TEXT,
      article_title TEXT NOT NULL,
      article_url TEXT NOT NULL,
      source TEXT DEFAULT 'tawjihnet',
      is_expired BOOLEAN DEFAULT FALSE,
      created_at TIMESTAMP WITH TIME ZONE DEFAULT TIMEZONE('utc', NOW()),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT TIMEZONE('utc', NOW()),
      
      UNIQUE(school_name, deadline_date, event_type)
    );

    CREATE INDEX IF NOT EXISTS idx_deadline_date ON school_deadlines(deadline_date);
    CREATE INDEX IF NOT EXISTS idx_school_name ON school_deadlines(school_name);
    CREATE INDEX IF NOT EXISTS idx_is_expired ON school_deadlines(is_expired);
  `;

    console.log('Database schema:', schema);
    return schema;
}

// Source priority for semantic deduplication (higher number = higher priority)
const SOURCE_PRIORITY = {
    'enssup.gov.ma (bourses)': 2,
    'tawjihnet': 1,
};
const getSourcePriority = (src) => SOURCE_PRIORITY[src] ?? 1;

// Noms génériques/invalides à ne jamais insérer.
// Les valeurs doivent être en forme normalisée : lowercase, sans accents, sans ponctuation.
const BLACKLISTED_NAMES = new Set([
    'unknown',
    'orientationscolaire',              // 'Orientation Scolaire'
    'systemeeducatifmarocain',          // 'Système Éducatif Marocain'
    'general',
    'generale',
    'maroc',
    'educationnationale',               // 'Education Nationale'
    'ministeredeleducationnationale',   // 'Ministère de l'Éducation Nationale'
    'ministeredeleducation',
]);

/**
 * Normalise une chaîne : lowercase, sans accents, sans ponctuation.
 * @param {string} str
 * @param {number} length - Nombre de caractères à retenir (0 = tout)
 * @returns {string}
 */
function normalizeStr(str, length = 0) {
    const normalized = (str || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
    return length > 0 ? normalized.substring(0, length) : normalized;
}

/**
 * Pour les entrées "Bourses X" ou "Bourse X", extrait le premier mot-clé pays/institution (X).
 * "Bourses Kazakhstan MNU" → "kazakhstan"
 * "Bourse Indonésie (Padjadjaran)" → "indonesie"
 * Retourne null si ce n'est pas une bourse.
 * @param {string} name
 * @returns {string|null}
 */
function extractBourseKey(name) {
    const norm = (name || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const match = norm.match(/^bourses?\s+([a-z]{3,})/);
    return match ? match[1] : null;
}

/**
 * Extrait les N premiers mots normalisés d'un nom (fallback pour non-bourses).
 * @param {string} str
 * @param {number} n
 * @returns {string}
 */
function firstWordsNormalized(str, n = 5) {
    return (str || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .split(/\s+/)
        .slice(0, n)
        .join(' ');
}

/**
 * Insert a new deadline into the database
 * @param {Object} deadline - Deadline object with school_name, deadline_date, event_type, article_title, article_url, source
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export async function insertDeadline(deadline) {
    try {
        // La table Supabase existante a des colonnes *_key NOT NULL pour chaque champ texte.
        // On dérive des clés normalisées (slug ascii, tirets).
        const toKey = (str) => (str || 'unknown')
            .toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        const newSource = deadline.source || 'tawjihnet';
        const newPriority = getSourcePriority(newSource);

        // ── Liste noire ─────────────────────────────────────────────────────────
        const nameNormalized = normalizeStr(deadline.schoolName);
        if (
            !deadline.schoolName ||
            deadline.schoolName.length < 5 ||
            BLACKLISTED_NAMES.has(nameNormalized)
        ) {
            return { success: false, error: 'blacklisted', message: `Nom ignoré : "${deadline.schoolName}"` };
        }
        // ── Fin liste noire ─────────────────────────────────────────────────────

        // ── Semantic deduplication ──────────────────────────────────────────────
        if (deadline.deadlineDate) {
            const bourseKey = extractBourseKey(deadline.schoolName);

            let similar = [];

            if (bourseKey) {
                // Stratégie bourses : même pays/institution clé (premier mot après "Bourses")
                // ex: "Bourses Kazakhstan" et "Bourses Kazakhstan MNU" → bourseKey = "kazakhstan"
                const { data } = await supabase
                    .from('school_deadlines')
                    .select('id, source, school_name')
                    .eq('deadline_date', deadline.deadlineDate)
                    .ilike('school_name', `%${bourseKey}%`);
                // Vérification : la ligne existante doit aussi être une bourse avec la même clé
                similar = (data || []).filter(r => extractBourseKey(r.school_name) === bourseKey);
            } else {
                // Stratégie générale : préfixe 15 chars normalisé OU 5 premiers mots identiques
                const likePrefix = toKey(deadline.schoolName).substring(0, 15);
                const prefix15 = normalizeStr(deadline.schoolName, 15);
                const words5 = firstWordsNormalized(deadline.schoolName, 5);

                const { data } = await supabase
                    .from('school_deadlines')
                    .select('id, source, school_name, school_name_key')
                    .eq('deadline_date', deadline.deadlineDate)
                    .like('school_name_key', `${likePrefix}%`);

                similar = (data || []).filter(r =>
                    normalizeStr(r.school_name, 15) === prefix15 ||
                    firstWordsNormalized(r.school_name, 5) === words5
                );
            }

            if (similar.length > 0) {
                const maxExistingPriority = Math.max(...similar.map(r => getSourcePriority(r.source)));

                if (newPriority > maxExistingPriority) {
                    const idsToDelete = similar
                        .filter(r => getSourcePriority(r.source) < newPriority)
                        .map(r => r.id);
                    if (idsToDelete.length > 0) {
                        await supabase.from('school_deadlines').delete().in('id', idsToDelete);
                        console.log(`  🔄 Replaced ${idsToDelete.length} duplicate(s) with ${newSource} data for "${deadline.schoolName}"`);
                    }
                    // fall through to insert
                } else {
                    return { success: false, error: 'duplicate', message: 'Semantic duplicate (lower or equal priority)' };
                }
            }
        }
        // ── End deduplication ───────────────────────────────────────────────────

        const { data, error } = await supabase
            .from('school_deadlines')
            .insert([{
                school_name: deadline.schoolName,
                school_name_key: toKey(deadline.schoolName),
                deadline_date: deadline.deadlineDate,
                event_type: deadline.eventType,
                event_type_key: toKey(deadline.eventType),
                article_title: deadline.originalTitle,
                article_url: deadline.url,
                source: newSource,
                is_expired: deadline.deadlineDate ? new Date(deadline.deadlineDate) < new Date() : false
            }])
            .select();

        if (error) {
            // Check if error is due to unique constraint (duplicate)
            if (error.code === '23505') {
                return { success: false, error: 'duplicate', message: 'Deadline already exists' };
            }
            throw error;
        }

        return { success: true, data };
    } catch (error) {
        console.error('Error inserting deadline:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Insert multiple deadlines, skipping duplicates
 * @param {Array} deadlines - Array of deadline objects
 * @returns {Promise<{inserted: number, skipped: number, errors: number, results: Array}>}
 */
export async function bulkInsertDeadlines(deadlines) {
    const results = {
        inserted: 0,
        skipped: 0,
        blacklisted: 0,
        errors: 0,
        details: []
    };

    for (const deadline of deadlines) {
        const result = await insertDeadline(deadline);

        if (result.success) {
            results.inserted++;
            results.details.push({ ...deadline, status: 'inserted' });
        } else if (result.error === 'duplicate') {
            results.skipped++;
            results.details.push({ ...deadline, status: 'duplicate' });
        } else if (result.error === 'blacklisted') {
            results.blacklisted++;
            results.details.push({ ...deadline, status: 'blacklisted' });
        } else {
            results.errors++;
            console.error(`  ❌ Insert error for "${deadline.schoolName}": ${result.error}`);
            results.details.push({ ...deadline, status: 'error', error: result.error });
        }
    }

    return results;
}

/**
 * Get all deadlines, optionally filtered
 * @param {Object} filters - Optional filters { futureOnly, eventType, limit }
 * @returns {Promise<Array>}
 */
export async function getDeadlines(filters = {}) {
    try {
        let query = supabase
            .from('school_deadlines')
            .select('*');

        // Filter by future deadlines only
        if (filters.futureOnly) {
            const today = new Date().toISOString().split('T')[0];
            query = query.gte('deadline_date', today);
        }

        // Filter by event type
        if (filters.eventType) {
            query = query.eq('event_type', filters.eventType);
        }

        // Filter by school name (partial match)
        if (filters.schoolName) {
            query = query.ilike('school_name', `%${filters.schoolName}%`);
        }

        // Sort by deadline date (closest first)
        query = query.order('deadline_date', { ascending: true, nullsLast: true });

        // Limit results
        if (filters.limit) {
            query = query.limit(filters.limit);
        }

        const { data, error } = await query;

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error fetching deadlines:', error);
        return [];
    }
}

/**
 * Update expired status for deadlines
 * @returns {Promise<number>} Number of updated records
 */
export async function updateExpiredDeadlines() {
    try {
        const today = new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
            .from('school_deadlines')
            .update({ is_expired: true })
            .lt('deadline_date', today)
            .eq('is_expired', false)
            .select();

        if (error) throw error;

        return data?.length || 0;
    } catch (error) {
        console.error('Error updating expired deadlines:', error);
        return 0;
    }
}

/**
 * Delete a deadline by ID
 * @param {string} id - Deadline ID
 * @returns {Promise<boolean>}
 */
export async function deleteDeadline(id) {
    try {
        const { error } = await supabase
            .from('school_deadlines')
            .delete()
            .eq('id', id);

        if (error) throw error;

        return true;
    } catch (error) {
        console.error('Error deleting deadline:', error);
        return false;
    }
}

/**
 * Get statistics about deadlines
 * @returns {Promise<Object>}
 */
export async function getDeadlineStats() {
    try {
        const { data: all, error: allError } = await supabase
            .from('school_deadlines')
            .select('*');

        if (allError) throw allError;

        const today = new Date();
        const futureDeadlines = all.filter(d => d.deadline_date && new Date(d.deadline_date) >= today);
        const expiredDeadlines = all.filter(d => d.deadline_date && new Date(d.deadline_date) < today);
        const withoutDates = all.filter(d => !d.deadline_date);

        const eventTypes = [...new Set(all.map(d => d.event_type))];
        const schools = [...new Set(all.map(d => d.school_name))];

        return {
            total: all.length,
            future: futureDeadlines.length,
            expired: expiredDeadlines.length,
            withoutDates: withoutDates.length,
            eventTypes: eventTypes.length,
            schools: schools.length,
            eventTypesList: eventTypes,
            schoolsList: schools
        };
    } catch (error) {
        console.error('Error getting stats:', error);
        return {
            total: 0,
            future: 0,
            expired: 0,
            withoutDates: 0,
            eventTypes: 0,
            schools: 0
        };
    }
}

/**
 * Search deadlines by keyword
 * @param {string} keyword - Search keyword
 * @returns {Promise<Array>}
 */
export async function searchDeadlines(keyword) {
    try {
        const { data, error } = await supabase
            .from('school_deadlines')
            .select('*')
            .or(`school_name.ilike.%${keyword}%,article_title.ilike.%${keyword}%,event_type.ilike.%${keyword}%`)
            .order('deadline_date', { ascending: true, nullsLast: true });

        if (error) throw error;

        return data || [];
    } catch (error) {
        console.error('Error searching deadlines:', error);
        return [];
    }
}

/**
 * Vide entièrement la table school_deadlines (nécessite SUPABASE_SERVICE_ROLE_KEY).
 * @returns {Promise<boolean>}
 */
export async function truncateDeadlines() {
    const client = supabaseAdmin || supabase;
    if (!supabaseAdmin) {
        console.warn('  ⚠️  SUPABASE_SERVICE_ROLE_KEY manquant — tentative avec clé anon (peut échouer si RLS actif)');
    }
    try {
        const { error } = await client
            .from('school_deadlines')
            .delete()
            .gte('created_at', '2000-01-01');
        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error truncating school_deadlines:', error);
        return false;
    }
}
